import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./../styles/About.css";

// ── Asset Imports ──
import droneImg from "../../assets/dronecov.webp";
import dreamyImg from "../../assets/About/dreamy.webp";
import boatImg from "../../assets/About/boat.webp";
import bunnyImg from "../../assets/About/bunny.webp";
import waterImg from "../../assets/About/water.webp";
import digiImg from "../../assets/About/digi.webp";
import roadImg from "../../assets/About/road.webp";
import cityImg from "../../assets/About/city.webp";
import blueImg from "../../assets/About/blue.webp";
import paperBg from "../../assets/About/paper.webp";

// ── Sound Utilities & Assets ──
import { sounds } from "../Utils/soundEffects";
import nodeHoverSfx from "../../assets/sounds/node-hover.mp3"; // Or reuse card-hover.mp3

gsap.registerPlugin(ScrollTrigger);

const ABOUT_NODES = [
  // ── Top Left: Top Photography ──
  {
    id: "top-photography",
    type: "rect",
    title: "Wander",
    curvePath: "M929.361 474.245C929.361 280 840 195 380 195",
    img: cityImg,
    modal: {
      layoutType: "paper-scroll-landscape",
      paperBg: paperBg,
      desc: "I enjoy taking trips to places, wandering without any stress, and coming back with a camera roll full of little moments I want to remember and look at later.",
      innerImg: cityImg,
    },
  },
  {
    id: "water1",
    type: "square",
    title: "Art",
    curvePath: "M929.361 474.246C929.361 330 495 490 495 355",
    img: boatImg,
    modal: {
      layoutType: "paper-scroll",
      paperBg: paperBg,
      mainTitleTop: "A Lakeside",
      mainTitleBottom: "View",
      desc: "This is one my favourite paintings I have done. I used acrylic color in this one, trying to make it look like a watercolor art 🥲",
      innerImg: boatImg,
    },
  },

  // ── Bottom Left: Bottom Photography ──
  {
    id: "bottom-photography",
    type: "rect",
    title: "Photography",
    curvePath: "M929.361 474.25C929.361 605 760 580 520 580",
    img: blueImg,
    modal: {
      layoutType: "paper-scroll-landscape",
      paperBg: paperBg,
      desc: "Mountains are one of my favourite things to photograph, especially the different views, skies, and little twinkling lights along the way.",
      innerImg: blueImg,
    },
  },
  {
    id: "char",
    type: "square-sm",
    title: "Char",
    curvePath: "M929.361 474.251C1035 570 910 775 685 775",
    img: bunnyImg,
    modal: {
      layoutType: "paper-scroll",
      paperBg: paperBg,
      mainTitleTop: "Bunny Girl",
      mainTitleBottom: "Character Design",
      desc: "I tried making a bunny character design in this one using Krita. This was my first character design!🩷",
      innerImg: bunnyImg,
    },
  },

  // ── Top Right: Water 2 ──
  {
    id: "water2",
    type: "square",
    title: "Acrylic",
    curvePath: "M929.416 474.247C780 230 1170 350 1240 215",
    img: dreamyImg,
    modal: {
      layoutType: "paper-scroll-dual",
      paperBg: paperBg,
      desc: "This one is about Acrylic paintings. It's a medium which I have still not been able to know how to use properly, but these were some good imperfect tries 👀",
      leftImg: dreamyImg,
      rightImg: roadImg,
    },
  },
  {
    id: "watercol",
    type: "rect",
    title: "Water Art",
    curvePath: "M929.5 474.321C929.5 350 1100 380 1355 380",
    img: waterImg,
    modal: {
      layoutType: "paper-scroll",
      paperBg: paperBg,
      mainTitleTop: "A Hut in",
      mainTitleBottom: "a Forest",
      desc: "Made this one using watercolors, one of more difficult mediums to control, but still one my favourite mediums and it makes things feel really dreamy and soothing... 🎨",
      innerImg: waterImg,
    },
  },

  // ── Bottom Right: Digi Art ──
  {
    id: "digi-art",
    type: "square",
    title: "Digiart",
    curvePath: "M929.359 474.252C935 585 1010 585 1320 585",
    img: digiImg,
    modal: {
      layoutType: "paper-scroll",
      paperBg: paperBg,
      mainTitleTop: "Digital Art",
      mainTitleBottom: "The Witch House",
      desc: "I made this one using Krita. This one was one of my first attempts in creating a digital art where I actually have to mind the shadows and the little details, although it still feels a bit incomplete 🥲 ",
      innerImg: digiImg,
    },
  },

  // ── Bottom Right: Research ──
  {
    id: "research",
    type: "rect",
    title: "research",
    curvePath: "M929.359 474.246C895 680 1060 750 1440 750",
    img: droneImg,
    modal: {
      layoutType: "paper-scroll",
      paperBg: paperBg,
      mainTitleTop: "Academic Research",
      mainTitleBottom: "Topic: Drones",
      desc: "I am genuinely interested in research and getting to know how we can make things work and get them to be better, I also have a research paper and a journal about drone movement optimzations. ",
      innerImg: droneImg,
    },
  },
];

export default function About() {
  const containerRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [endpoints, setEndpoints] = useState({});

  // 1. Preload sound effect on mount
  useEffect(() => {
    sounds.loadSound("nodeHover", nodeHoverSfx);

    const unlockAudio = () => {
      sounds.init();
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };

    window.addEventListener("pointerdown", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  // Calculate curve ends
  useEffect(() => {
    if (!containerRef.current) return;
    const computedPositions = {};
    const paths = containerRef.current.querySelectorAll(".thread-curve-path");

    paths.forEach((path) => {
      const id = path.getAttribute("data-id");
      const totalLen = path.getTotalLength();
      const endPoint = path.getPointAtLength(totalLen);

      computedPositions[id] = {
        left: (endPoint.x / 1858) * 100,
        top: (endPoint.y / 949) * 100,
      };
    });

    setEndpoints(computedPositions);
  }, []);

  // Lock outer background scroll when paper modal opens
  useEffect(() => {
    if (selectedNode) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedNode]);

  // Entrance & floating animation loop
  useEffect(() => {
    if (Object.keys(endpoints).length === 0 || !containerRef.current) return;

    const ctx = gsap.context(() => {
      const paths = containerRef.current.querySelectorAll(".thread-curve-path");

      paths.forEach((path) => {
        const length = path.getTotalLength();
        gsap.set(path, {
          strokeDasharray: length,
          strokeDashoffset: length,
        });
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 70%",
          toggleActions: "play none none reverse",
        },
      });

      tl.fromTo(
        ".about-center-title",
        { opacity: 0, scale: 0.82 },
        { opacity: 1, scale: 1, duration: 0.8, ease: "power3.out" }
      )
      .to(
        ".thread-curve-path",
        {
          strokeDashoffset: 0,
          duration: 0.9,
          stagger: 0.18,
          ease: "power3.inOut",
        },
        "-=0.2"
      )
      .fromTo(
        ".about-node-card",
        { opacity: 0, scale: 0.65 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.6,
          stagger: 0.12,
          ease: "back.out(1.6)",
        },
        "-=1.1"
      );

      containerRef.current.querySelectorAll(".about-node-card").forEach((node, i) => {
        gsap.to(node, {
          y: i % 2 === 0 ? "+=6" : "-=6",
          x: i % 3 === 0 ? "+=4" : "-=4",
          rotation: i % 2 === 0 ? 0.6 : -0.6,
          duration: 3 + (i % 3) * 0.6,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          delay: 1.5,
        });
      });
    }, containerRef);

    return () => ctx.revert();
  }, [endpoints]);

  return (
    <section className="about-network-section" ref={containerRef}>
      <div className="about-halftone-vignette" />

      <div className="about-viewport-stage">
        <svg
          className="about-threads-svg"
          viewBox="0 0 1858 949"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {ABOUT_NODES.map((node) => (
            <path
              key={node.id}
              data-id={node.id}
              d={node.curvePath}
              className="thread-curve-path"
            />
          ))}
        </svg>

        <div className="about-center-anchor">
          <h1 className="about-center-title">About</h1>
        </div>

        {ABOUT_NODES.map((node) => {
          const pos = endpoints[node.id] || { left: 50, top: 50 };

          return (
            <div
              key={node.id}
              className={`about-node-card node-${node.type}`}
              style={{
                left: `${pos.left}%`,
                top: `${pos.top}%`,
              }}
              /* Trigger sound on mouse enter */
              onMouseEnter={() => sounds.play("nodeHover", 0.25)}
              onClick={() => setSelectedNode(node)}
            >
              <div className="node-media-surface">
                <img
                  src={node.img}
                  alt={node.title}
                  className="node-img-asset"
                  loading="lazy"
                  decoding="async"
                />
                <div className="node-dark-overlay" />
              </div>
              <span className="node-floating-label">{node.title}</span>
            </div>
          );
        })}
      </div>

      {selectedNode && (
        <div
          className="about-modal-backdrop"
          onClick={() => setSelectedNode(null)}
        >
          {/* Variant 1: Dual-Image Scroll View */}
          {selectedNode.modal.layoutType === "paper-scroll-dual" ? (
            <div
              className="about-paper-modal-panel dual-view"
              data-lenis-prevent
              style={{ backgroundImage: `url(${selectedNode.modal.paperBg})` }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="about-paper-modal-close"
                onClick={() => setSelectedNode(null)}
                aria-label="Close"
              >
                ✕
              </button>

              <div className="paper-dual-body">
                <div className="paper-dual-gallery">
                  <div className="paper-dual-frame">
                    <img
                      src={selectedNode.modal.leftImg}
                      alt="Left visual"
                      className="about-paper-art-img"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="paper-dual-frame">
                    <img
                      src={selectedNode.modal.rightImg}
                      alt="Right visual"
                      className="about-paper-art-img"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
                <p className="paper-dual-caption">{selectedNode.modal.desc}</p>
              </div>
            </div>
          ) : selectedNode.modal.layoutType === "paper-scroll-landscape" ? (
            /* Variant 2: Landscape Scroll View */
            <div
              className="about-paper-modal-panel landscape-view"
              data-lenis-prevent
              style={{ backgroundImage: `url(${selectedNode.modal.paperBg})` }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="about-paper-modal-close"
                onClick={() => setSelectedNode(null)}
                aria-label="Close"
              >
                ✕
              </button>

              <div className="paper-landscape-body">
                <div className="paper-landscape-frame">
                  <img
                    src={selectedNode.modal.innerImg}
                    alt="Scenic photography"
                    className="about-paper-art-img"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <p className="paper-landscape-caption">{selectedNode.modal.desc}</p>
              </div>
            </div>
          ) : (
            /* Variant 3: Standard Single-Item Paper Scroll */
            <div
              className="about-paper-modal-panel"
              data-lenis-prevent
              style={{ backgroundImage: `url(${selectedNode.modal.paperBg})` }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="about-paper-modal-close"
                onClick={() => setSelectedNode(null)}
                aria-label="Close"
              >
                ✕
              </button>

              <div className="about-paper-modal-body">
                <div className="about-paper-art-frame">
                  <img
                    src={selectedNode.modal.innerImg}
                    alt={selectedNode.modal.mainTitleTop}
                    className="about-paper-art-img"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <div className="about-paper-content-side">
                  <div className="about-paper-headings">
                    <h2 className="paper-title-bold">{selectedNode.modal.mainTitleTop}</h2>
                    <h3 className="paper-title-italic">{selectedNode.modal.mainTitleBottom}</h3>
                  </div>
                  <p className="about-paper-desc">{selectedNode.modal.desc}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}